import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Dialog, DialogContent, DialogTitle, LinearProgress, Typography } from '@mui/material';
import { Translate, Storage } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faFileExcel, faCheckCircle, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { BackgroundExportManager } from './background-export-manager';

interface ExportDialogProps {
  open: boolean;
  status: string;
  progress: number;
  loading: boolean;
  exportType?: 'excel' | 'pdf';
}

export const ExportDialog: React.FC<ExportDialogProps> = ({
  open,
  status: initialStatus,
  progress: initialProgress,
  loading,
  exportType = 'excel',
}) => {
  const isArabic = Storage.session.get('locale') === 'ar-ly';
  const [progress, setProgress] = useState(initialProgress);
  const [status, setStatus] = useState(initialStatus);
  const [isBackground, setIsBackground] = useState(false);

  useEffect(() => {
    setProgress(initialProgress);
    setStatus(initialStatus);
  }, [initialProgress, initialStatus]);

  useEffect(() => {
    if (!open) return;

    const exportManager = BackgroundExportManager.getInstance();

    const unsubscribe = exportManager.onProgress((newProgress, newStatus) => {
      setProgress(newProgress);
      setStatus(newStatus);
    });

    const storedProgress = exportManager.getStoredProgress();
    if (storedProgress && loading) {
      setProgress(storedProgress.progress);
      setStatus(storedProgress.status);
    }

    const handleVisibilityChange = () => {
      const hidden = document.hidden;
      setIsBackground(hidden);

      if (hidden && loading) {
        console.warn('Export continuing in background...');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [open, loading]);

  useEffect(() => {
    if (open && loading) {
      const checkStoredProgress = () => {
        try {
          const exportState = localStorage.getItem('export-state');
          if (exportState) {
            const state = JSON.parse(exportState);
            const timeDiff = Date.now() - state.timestamp;

            if (timeDiff < 30000) {
              setProgress(state.progress);
              setStatus(state.status);
            }
          }
        } catch (e) {
          // Ignore parsing errors
        }
      };

      checkStoredProgress();

      const interval = setInterval(checkStoredProgress, 1000);

      return () => clearInterval(interval);
    }
  }, [open, loading]);

  const getIconObject = () => {
    switch (exportType) {
      case 'pdf':
        return faFilePdf;
      case 'excel':
      default:
        return faFileExcel;
    }
  };

  const getTitle = () => {
    switch (exportType) {
      case 'pdf':
        return 'global.menu.export-report.exportingToPDF';
      case 'excel':
      default:
        return 'global.menu.export-report.exportingToExcel';
    }
  };

  const getColor = () => {
    switch (exportType) {
      case 'pdf':
        return '#DC2626';
      case 'excel':
      default:
        return '#16A34A';
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <CircularProgress size={24} sx={{ color: getColor() }} />
          <FontAwesomeIcon icon={getIconObject()} style={{ fontSize: '24px', color: getColor() }} />
          <Typography variant="h6">
            <Translate contentKey={getTitle()} />
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {status}
          </Typography>

          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, progress))}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'rgba(0,0,0,0.1)',
              transform: isArabic ? 'scaleX(-1)' : 'none',
              '& .MuiLinearProgress-bar': {
                backgroundColor: getColor(),
                transform: isArabic ? 'scaleX(-1)' : 'none',
              },
            }}
          />

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {Math.round(progress)}%
          </Typography>

          {loading && progress < 100 && (
            <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
              <Translate contentKey="global.menu.export-report.exportingMessage" />
            </Typography>
          )}

          {progress === 100 && (
            <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
              <FontAwesomeIcon icon={faCheckCircle} /> <Translate contentKey="global.menu.export-report.completed" />
            </Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
